import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  Show,
  For,
} from "solid-js";
import { A, useNavigate, useSearchParams } from "@solidjs/router";
import {
  Badge,
  Button,
  Input,
  Modal,
  Spinner,
  EmptyState,
} from "../components/ui";
import { useApi } from "../hooks";
import {
  listProjects,
  createProject,
  type ProjectListResponse,
  type Project,
} from "../api";
import { useProjectCreated } from "../contexts/ProjectContext";
import { tenantRole } from "../stores/currentUserStore";
import { canCreateProject } from "../utils/permissions";

const ProjectCard: Component<{ project: Project }> = (props) => {
  const createdDate = () => {
    const d = new Date(props.project.created_at);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <A
      href={`/projects/${props.project.id}/settings`}
      class="block bg-surface rounded border border-line p-5 hover:border-line-strong transition-all"
    >
      <div class="flex items-start justify-between mb-2">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-sunken border border-line rounded flex items-center justify-center">
            <span class="text-ink-2 font-bold text-xs">
              {props.project.prefix ??
                props.project.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <h3 class="text-base font-semibold text-ink">{props.project.name}</h3>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Show when={props.project.archived_at}>
            <Badge variant="archived">Archived</Badge>
          </Show>
          {/* Archiving implies read-only, so the "Archived" badge stands in
              for both rather than stacking two badges saying the same thing. */}
          <Show when={props.project.read_only && !props.project.archived_at}>
            <Badge variant="readOnly">Read-only</Badge>
          </Show>
        </div>
      </div>
      <Show when={props.project.description}>
        <p class="text-sm text-ink-2 mb-3 line-clamp-2">
          {props.project.description}
        </p>
      </Show>
      <div class="text-xs text-ink-3">Created {createdDate()}</div>
    </A>
  );
};

export const ProjectsPage: Component = () => {
  const navigate = useNavigate();
  const onProjectCreated = useProjectCreated();
  const { data, loading, error } = useApi<ProjectListResponse>(listProjects);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCreate, setShowCreate] = createSignal(false);

  // The rail's project switcher offers "New project" as its own row, so that
  // row has to land on the form, not just on this page. Tracked rather than
  // read once: arriving from "All projects" leaves the user already on
  // /projects, where the switcher's next navigation changes only the query
  // and this page does not remount. The param is consumed as it opens, so
  // dismissing the modal cannot reopen it. Gated on the same permission as
  // the button below, so a hand-typed URL cannot surface a form the backend
  // would refuse.
  createEffect(() => {
    if (searchParams.new !== "1") return;
    setSearchParams({ new: undefined }, { replace: true });
    if (canCreateProject(tenantRole())) setShowCreate(true);
  });
  const [createName, setCreateName] = createSignal("");
  const [createDescription, setCreateDescription] = createSignal("");
  const [createPrefix, setCreatePrefix] = createSignal("");
  const [creating, setCreating] = createSignal(false);
  const [createError, setCreateError] = createSignal("");
  const [showArchived, setShowArchived] = createSignal(false);

  const activeProjects = createMemo(
    () => data()?.projects.filter((p) => !p.archived_at) ?? [],
  );
  const archivedProjects = createMemo(
    () => data()?.projects.filter((p) => p.archived_at) ?? [],
  );

  const handleCreate = async () => {
    const name = createName().trim();
    if (!name) {
      setCreateError("Project name is required");
      return;
    }
    setCreating(true);
    setCreateError("");
    // Read before the await (FLOW-814): a signal read after an await can
    // observe state the create itself changed.
    const isFirstProject = data()?.projects.length === 0;
    try {
      const project = await createProject({
        name,
        description: createDescription().trim() || undefined,
        prefix: createPrefix().trim() || undefined,
      });
      onProjectCreated(project);
      setShowCreate(false);
      setCreateName("");
      setCreateDescription("");
      setCreatePrefix("");
      navigate(isFirstProject ? "/plan" : "/board");
    } catch (e) {
      setCreateError(
        e instanceof Error ? e.message : "Failed to create project",
      );
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setShowCreate(false);
    setCreateName("");
    setCreateDescription("");
    setCreatePrefix("");
    setCreateError("");
  };

  return (
    <div class="h-full overflow-auto">
      <div class="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div class="flex items-center justify-between mb-6">
          <div>
            <h1 class="text-2xl font-bold text-ink">Projects</h1>
            <p class="text-sm text-ink-3 mt-1">
              Manage your projects and their settings
            </p>
          </div>
          <Show when={canCreateProject(tenantRole())}>
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              New Project
            </Button>
          </Show>
        </div>

        {/* Explanation for org members who cannot create a project */}
        <Show when={!canCreateProject(tenantRole())}>
          <div class="mb-6 rounded border border-signal-amber/25 bg-signal-amber-tint px-4 py-3 text-sm text-signal-amber">
            Only an organization admin can create projects. Ask an admin to
            create one or invite you to an existing project.
          </div>
        </Show>

        {/* Loading */}
        <Show when={loading()}>
          <div class="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Show>

        {/* Error */}
        <Show when={error()}>
          <div class="bg-signal-red-tint border border-signal-red/25 rounded p-4 text-signal-red text-sm">
            Failed to load projects. Please try again.
          </div>
        </Show>

        {/* Project list */}
        <Show when={data()}>
          {(result) => (
            <Show
              when={result().projects.length > 0}
              fallback={
                <EmptyState
                  title="No projects yet"
                  description="Create your first project to get started."
                  action={
                    <Show when={canCreateProject(tenantRole())}>
                      <Button
                        variant="primary"
                        onClick={() => setShowCreate(true)}
                      >
                        Create Project
                      </Button>
                    </Show>
                  }
                />
              }
            >
              <Show when={activeProjects().length > 0}>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <For each={activeProjects()}>
                    {(project) => <ProjectCard project={project} />}
                  </For>
                </div>
              </Show>

              <Show when={archivedProjects().length > 0}>
                <section class="mt-8">
                  <button
                    type="button"
                    aria-expanded={showArchived()}
                    aria-controls="archived-projects"
                    onClick={() => setShowArchived(!showArchived())}
                    class="hit-area flex items-center gap-2 text-sm font-medium text-ink-2 hover:text-ink focus-ring rounded-sm"
                  >
                    <svg
                      class={
                        "w-4 h-4 text-ink-3 flex-shrink-0 transition-transform " +
                        (showArchived() ? "rotate-90" : "")
                      }
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    Archived ({archivedProjects().length})
                  </button>
                  <div id="archived-projects">
                    <Show when={showArchived()}>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <For each={archivedProjects()}>
                          {(project) => <ProjectCard project={project} />}
                        </For>
                      </div>
                    </Show>
                  </div>
                </section>
              </Show>
            </Show>
          )}
        </Show>

        {/* Create Project Modal */}
        <Modal
          open={showCreate()}
          onClose={resetCreateForm}
          title="Create Project"
          size="md"
        >
          <div class="space-y-4">
            <Input
              label="Project Name"
              placeholder="My Project"
              value={createName()}
              onInput={setCreateName}
              maxLength={255}
            />
            <Input
              label="Description"
              placeholder="What is this project about?"
              value={createDescription()}
              onInput={setCreateDescription}
              isTextarea
              rows={3}
            />
            <Input
              label="Prefix"
              placeholder="e.g. PRJ (auto-derived if empty)"
              value={createPrefix()}
              onInput={setCreatePrefix}
              maxLength={10}
            />
            <Show when={createError()}>
              <p class="text-sm text-signal-red">{createError()}</p>
            </Show>
            <div class="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={resetCreateForm}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreate}
                loading={creating()}
                disabled={creating()}
              >
                Create
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};
